import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import TTSForm from './components/TTSForm';
import VoiceControls from './components/VoiceControls';
import AudioPlayer from './components/AudioPlayer';
import GoogleSignIn from './components/GoogleSignIn';
import UserProfile from './components/UserProfile';
import AppHeader from './components/AppHeader';
import History from './components/History';
import Profile from './components/Profile';
import Sidebar from './components/Sidebar';
import VoiceUpload from './components/VoiceUpload';
import APIAccess from './components/APIAccess';
import Landing from './components/Landing';
import CreditTopup from './components/CreditTopup';
import AdminVoices from './components/AdminVoices';
import Privacy from './components/Privacy';
import ContactUs from './components/ContactUs';
import APIDocs from './components/APIDocs';
import Blog from './components/Blog';
import { Volume2 } from 'lucide-react';
import apiService from './api/api';
import { useSEO } from './hooks/useSEO';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const initRef = useRef(false);
  const voicesLoadedRef = useRef(false);
  const customVoicesLoadedRef = useRef(false);
  const [apiConnected, setApiConnected] = useState(false);
  const [audioResult, setAudioResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState('');
  const [voices, setVoices] = useState([]);
  const [customVoices, setCustomVoices] = useState([]);
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(5000);
  const [showSignIn, setShowSignIn] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [formData, setFormData] = useState({
    text: 'Hello and welcome to stick audio, the most natural AI text-to-speech converter.',
    voice: '',
    temperature: 0.8,
  });

  const fetchCredits = async () => {
    try {
      const data = await apiService.getUserCredits();
      setCredits(data.credits || 5000);
    } catch (err) {
      console.error('Error fetching credits:', err);
    }
  };

  // Check if user is already signed in on mount
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const savedUser = apiService.getUserFromStorage();
    if (savedUser) {
      setUser(savedUser);
      setShowSignIn(false);
      // Fetch credits after restoring user
      fetchCredits();
    }
    setAuthChecked(true);
  }, []);

  // Redirect unauthenticated users trying to access protected routes
  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (!authChecked) return;

    const protectedRoutes = ['/generate', '/history', '/voices', '/api-access', '/add-credits', '/profile', '/admin-voices'];
    const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));

    if (isProtectedRoute && showSignIn) {
      navigate('/');
    }
  }, [location.pathname, showSignIn, navigate, authChecked]);

  // Load built-in voices when on generate page
  useEffect(() => {
    if (location.pathname !== '/generate') {
      voicesLoadedRef.current = false;
      return;
    }

    if (voicesLoadedRef.current) {
      return;
    }

    voicesLoadedRef.current = true;

    const loadBuiltInVoices = async () => {
      try {
        const availableVoices = await apiService.getVoices();
        setVoices(availableVoices);
      } catch (err) {
        console.error('Error loading built-in voices:', err);
      }
    };

    loadBuiltInVoices();
  }, [location.pathname]);

  // Load custom voices when user is available on generate page
  useEffect(() => {
    if (location.pathname !== '/generate' || !user) {
      customVoicesLoadedRef.current = false;
      return;
    }

    if (customVoicesLoadedRef.current) {
      return;
    }

    customVoicesLoadedRef.current = true;

    const loadCustomVoices = async () => {
      try {
        const data = await apiService.getCustomVoices();
        const loadedCustomVoices = data.voices || [];
        setCustomVoices(loadedCustomVoices);

        // Auto-select first available voice (prefer custom over built-in)
        if (loadedCustomVoices.length > 0) {
          const firstCustomVoice = loadedCustomVoices[0].name;
          setFormData(prev => ({
            ...prev,
            voice: firstCustomVoice,
          }));
        } else if (voices.length > 0) {
          const firstBuiltInVoice = typeof voices[0] === 'string' ? voices[0] : voices[0].name;
          setFormData(prev => ({
            ...prev,
            voice: firstBuiltInVoice,
          }));
        }
      } catch (err) {
        console.error('Error loading custom voices:', err);
        setCustomVoices([]);
      }
    };

    loadCustomVoices();
  }, [location.pathname, user, voices]);

  const handleGenerateTTS = async (formData) => {
    setLoading(true);
    setError(null);
    setJobProgress(0);
    setJobStatus('Creating job...');

    try {
      // Prepare the request data - backend will resolve voice name to reference_audio
      const requestData = {
        text: formData.text,
        voice: formData.voice,
        temperature: formData.temperature,
        // Backend will use default values for: top_k, top_p, repetition_penalty
      };

      // Call async endpoint (now returns job_id immediately)
      const jobResult = await apiService.generateTTSWithUser(requestData);
      const jobId = jobResult.job_id;

      if (!jobId) {
        throw new Error('No job_id returned from server');
      }

      setJobStatus('Processing...');

      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await apiService.getJobStatus(jobId);

          setJobProgress(status.progress || 0);
          setJobStatus(status.status);

          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setAudioResult({
              status: 'success',
              audio_url: status.audio_url,
              file_name: status.file_name,
            });
            await fetchCredits();
            setLoading(false);
            setJobStatus('');
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setError(status.error_message || 'Generation failed');
            setLoading(false);
            setJobStatus('');
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          clearInterval(pollInterval);
          setError('Error checking job status');
          setLoading(false);
          setJobStatus('');
        }
      }, 5000); // Poll every 5 seconds

      // Safety timeout: stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (loading) {
          setError('Generation timed out. Please check History tab.');
          setLoading(false);
          setJobStatus('');
        }
      }, 600000); // 5 minutes
    } catch (err) {
      console.error('TTS Generation Error:', err);
      setError(err.detail || err.message || 'Error generating TTS');
      setLoading(false);
      setJobStatus('');
    }
  };

  const handleSignInSuccess = (userData) => {
    setUser(userData);
    setShowSignIn(false);
    setError(null);
    // Fetch credits after sign in
    fetchCredits();
    // Redirect to generate page after successful sign in
    navigate('/generate');
  };

  const handleSignInError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleSignOut = () => {
    apiService.signOut(); // Clear localStorage
    setUser(null);
    setShowSignIn(true);
    setAudioResult(null);
    navigate('/');
  };

  // Determine current active tab from location
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('history')) return 'history';
    if (path.includes('admin-voices')) return 'admin-voices';
    if (path.includes('voices')) return 'voices';
    if (path.includes('api-access')) return 'api-access';
    if (path.includes('add-credits')) return 'add-credits';
    if (path.includes('profile')) return 'profile';
    return 'generate';
  };

  // Get page title and subtitle based on current route
  const getPageTitleAndSubtitle = () => {
    const path = location.pathname;
    const pageInfo = {
      '/generate': { title: 'Text To Speech', subtitle: 'Convert text into natural speech' },
      '/history': { title: 'Generation History', subtitle: 'View and manage past generations' },
      '/voices': { title: 'Voice Library', subtitle: 'Built-in and Cloned voices' },
      '/api-access': { title: 'API', subtitle: 'Manage your API keys' },
      '/add-credits': { title: 'Credits', subtitle: 'Purchase additional credits' },
      '/profile': { title: 'Account Settings', subtitle: 'Manage your profile' },
      '/admin-voices': { title: 'Manage Voices', subtitle: 'Admin dashboard' },
    };
    return pageInfo[path] || { title: '', subtitle: '' };
  };

  // Determine SEO content based on route
  const getSEOContent = () => {
    const path = location.pathname;
    const seoConfig = {
      '/': {
        title: 'Stick Audio - AI Text-to-Speech Generator',
        description: 'Transform text into natural-sounding speech with AI-powered voices. Voice cloning, unlimited custom voices, REST API access, and enterprise security. Start free with 2,000 characters.',
        keywords: 'text-to-speech, TTS, AI voice generation, voice cloning, speech synthesis, API',
      },
      '/generate': {
        title: 'Generate Audio - Stick Audio Text-to-Speech',
        description: 'Convert your text to natural-sounding audio with Stick Audio. Multiple AI voices, real-time generation, and instant playback.',
        keywords: 'text-to-speech generator, voice synthesis, audio generation, convert text to speech',
      },
      '/history': {
        title: 'Generation History - Stick Audio',
        description: 'View and manage your TTS generation history with metadata, voice parameters, and timestamps.',
        keywords: 'TTS history, generation logs, voice synthesis history',
      },
      '/voices': {
        title: 'Voice Library - Stick Audio',
        description: 'Browse built-in voices and manage your custom cloned voices. Upload audio samples to create unique voice profiles.',
        keywords: 'voice library, voice cloning, custom voices, voice samples',
      },
      '/api-access': {
        title: 'API Access - Stick Audio Developer Dashboard',
        description: 'Manage your API keys and integrate Stick Audio TTS into your applications. REST API with code examples for cURL, Node.js, and Python.',
        keywords: 'API access, developer API, REST API, API keys, API documentation',
      },
      '/profile': {
        title: 'Account Settings - Stick Audio',
        description: 'Manage your Stick Audio account, view credits, and update your profile settings.',
        keywords: 'account settings, user profile, credit management',
      },
      '/add-credits': {
        title: 'Purchase Credits - Stick Audio',
        description: 'Buy additional credits for your Stick Audio account. Choose from Starter, Creator, or Professional plans.',
        keywords: 'purchase credits, pricing plans, credit topup, buy credits',
      },
      '/privacy': {
        title: 'Privacy Policy - Stick Audio',
        description: 'Read our privacy policy and understand how Stick Audio protects your data and privacy.',
        keywords: 'privacy policy, data protection, privacy',
      },
      '/contact-us': {
        title: 'Contact Us - Stick Audio Support',
        description: 'Get in touch with Stick Audio support team. We\'re here to help with any questions or issues.',
        keywords: 'contact us, support, customer service, help',
      },
      '/docs': {
        title: 'API Documentation - Stick Audio',
        description: 'Comprehensive API documentation for Stick Audio. Learn how to integrate text-to-speech into your applications with code examples in cURL, Node.js, and Python.',
        keywords: 'API documentation, REST API, integration guide, code examples',
      },
      '/blogs': {
        title: 'Blog - Stick Audio Text-to-Speech',
        description: 'Discover how Stick Audio is 5x cheaper than Eleven Labs and offers unlimited voice cloning at no extra cost. Learn about AI voice generation and TTS technology.',
        keywords: 'text-to-speech, Eleven Labs alternative, voice cloning, cheaper TTS, AI voices',
      },
    };

    return seoConfig[path] || seoConfig['/'];
  };

  // Update SEO based on current route
  useSEO(getSEOContent());

  // Render authenticated or unauthenticated based on showSignIn
  return (
    <Routes>
      {/* Public Routes - Accessible without authentication */}
      <Route path="/" element={
        <Landing
          isAuthenticated={!showSignIn}
          onSignInSuccess={handleSignInSuccess}
          onSignInError={handleSignInError}
        />
      } />

      <Route path="/privacy" element={<Privacy />} />

      <Route path="/contact-us" element={<ContactUs />} />

      <Route path="/docs" element={<APIDocs />} />

      <Route path="/blogs" element={<Blog />} />

      <Route path="/blogs/:slug" element={<Blog />} />

      <Route path="/landing" element={
        <Landing
          isAuthenticated={true}
          onSignInSuccess={handleSignInSuccess}
          onSignInError={handleSignInError}
        />
      } />

      {/* Authenticated Routes */}
      {!showSignIn && (
        <>
          <Route path="*" element={
            <div className="app-layout">
              {/* Sidebar */}
              <Sidebar activeTab={getActiveTab()} onTabChange={navigate} user={user} onSignOut={handleSignOut} />

              {/* Main Content */}
              <div className="app-main">
                {/* Header */}
                {(() => {
                  const pageInfo = getPageTitleAndSubtitle();
                  return <AppHeader credits={credits} error={error} title={pageInfo.title} subtitle={pageInfo.subtitle} />;
                })()}

                {/* Tab Content */}
                <div className="tab-content">
                  <Routes>
            <Route path="/generate" element={
              <div className="generate-tab">
                <div className="content-wrapper">
                  <div className="content-inner generate-container">
                  {/* Main Content - Left and Right Columns */}
                  <div className="generate-content-wrapper">
                    {/* Left Column - Text Input */}
                    <div className="text-input-column">
                      {/* Text Input Section */}
                      <div className="text-input-section">
                        <TTSForm
                          onSubmit={handleGenerateTTS}
                          loading={loading}
                          formData={formData}
                          setFormData={setFormData}
                          credits={credits}
                          jobProgress={jobProgress}
                          jobStatus={jobStatus}
                        />
                      </div>
                    </div>

                    {/* Right Column - Voice Controls */}
                    <div className="voice-controls-column">
                      <div className="voice-controls-section">
                        <VoiceControls
                          formData={formData}
                          onFormDataChange={setFormData}
                          voices={voices}
                          customVoices={customVoices}
                          loading={loading}
                        />
                      </div>

                      {/* Audio Output Section - Below Voice Controls */}
                      <div className="audio-output-section">
                        {audioResult ? (
                          <div className="w-full">
                            <div style={{ marginBottom: '16px' }}>
                              <h3 className="section-title">
                                <Volume2 size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                                Generated Audio
                              </h3>
                            </div>
                            <AudioPlayer audioUrl={audioResult.audio_url} fileName={audioResult.file_name} />
                          </div>
                        ) : (
                          <div className="helper-text-secondary">
                            <p className="helper-text">Generated audio will appear here</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            } />

            <Route path="/history" element={
              <div className="history-tab">
                {user ? (
                  <History key={audioResult?.file_name} />
                ) : (
                  <div className="auth-required">
                    <p>Please sign in to view your generation history</p>
                  </div>
                )}
              </div>
            } />

            <Route path="/voices" element={
              <div className="voices-tab">
                {user ? (
                  <VoiceUpload />
                ) : (
                  <div className="auth-required">
                    <p>Please sign in to manage custom voices</p>
                  </div>
                )}
              </div>
            } />

            <Route path="/api-access" element={
              <div className="api-access-tab">
                {user ? (
                  <APIAccess user={user} />
                ) : (
                  <div className="auth-required">
                    <p>Please sign in to access API settings</p>
                  </div>
                )}
              </div>
            } />

            <Route path="/add-credits" element={
              <div className="add-credits-tab">
                <CreditTopup user={user} />
              </div>
            } />

            <Route path="/profile" element={
              <div className="profile-tab">
                <Profile user={user} />
              </div>
            } />

            <Route path="/admin-voices" element={
              <div className="admin-voices-tab">
                {user?.email === 'maazkhansgsits@gmail.com' ? (
                  <AdminVoices />
                ) : (
                  <div className="auth-required">
                    <p>You do not have permission to access this resource</p>
                  </div>
                )}
              </div>
            } />
                  </Routes>
                </div>
              </div>
            </div>
          } />
        </>
      )}
    </Routes>
  );
}

export default App;
